import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, ButtonGroup, Button } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../../components/SearchableSelect';

import { generateMessagesTrendData } from '../utils/dataGenerators';
import { getMessagesTrendChartOptions } from '../configs/chartConfigs';
import { useMessagesTrendMetrics } from '../hooks/useMetrics';
import { getsbidoptions } from '../../../service/telemetricDashboardService';

// Static MSGID data for Raymond
const RAYMOND_MSGID_DATA = [
  { "MSGID": "5000", "DIR": "Outbound" },
  { "MSGID": "5006", "DIR": "Outbound" },
  { "MSGID": "5007", "DIR": "Inbound" },
  { "MSGID": "5007", "DIR": "Outbound" },
  { "MSGID": "500D", "DIR": "Outbound" },
  { "MSGID": "5012", "DIR": "Outbound" },
  { "MSGID": "5013", "DIR": "Outbound" },
  { "MSGID": "5014", "DIR": "Outbound" },
  { "MSGID": "6000", "DIR": "Inbound" },
  { "MSGID": "6002", "DIR": "Inbound" },
  { "MSGID": "6003", "DIR": "Inbound" },
  { "MSGID": "6004", "DIR": "Inbound" },
  { "MSGID": "7000", "DIR": "Inbound" },
  { "MSGID": "7000", "DIR": "Outbound" },
  { "MSGID": "9000", "DIR": "Outbound" },
  { "MSGID": "9001", "DIR": "Inbound" }
];

const RaymondMessagesTrendChart = ({ chartFilters, selectedManufacturer }) => {
  // Local state for message-specific filters
  const [direction, setDirection] = useState(null);
  const [messageTypes, setMessageTypes] = useState([]);
  const [sbidOptions, setSbidOptions] = useState([]);
  const [selectedSbids, setSelectedSbids] = useState([]);
  const [isDrilldownActive, setIsDrilldownActive] = useState(false);

  // Fetch SBID options from API
  useEffect(() => {
    async function fetchSbidOptions() {
      try {
        const resp = await getsbidoptions(selectedManufacturer);
        if (resp && resp.data) {
          setSbidOptions(
            resp.data.map((item) => ({ value: item.SBID, MSGID: item.MSGID, label: `${item.SBID} - ${item.Description}` }))
          );
        }
      } catch (err) {
        setSbidOptions([]);
      }
    }
    fetchSbidOptions();
  }, [selectedManufacturer]);

  // Fetch real data from API with backend filters only
  const { messagesTrendData, loading, error, isDrilldownData } = useMessagesTrendMetrics(selectedManufacturer, chartFilters, isDrilldownActive);

  // Get message type options based on direction
  const messageTypeOptions = useMemo(() => {
    // If no direction selected, show all message types
    if (!direction) {
      return RAYMOND_MSGID_DATA
        .filter(mt => mt.MSGID !== null)
        .map(mt => ({ value: mt.MSGID, label: mt.MSGID }))
        .filter((opt, index, self) =>
          index === self.findIndex((t) => t.value === opt.value)
        )
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    const directionCode = direction === 'inbound' ? 'Inbound' : 'Outbound';
    return RAYMOND_MSGID_DATA
      .filter(mt => mt.DIR === directionCode && mt.MSGID !== null)
      .map(mt => ({ value: mt.MSGID, label: mt.MSGID }))
      .filter((opt, index, self) =>
        index === self.findIndex((t) => t.value === opt.value)
      );
  }, [direction]);

  // Exit drilldown if message types change away from 6002
  useEffect(() => {
    if (direction !== 'inbound' || messageTypes[0] !== '6002' || messageTypes.length !== 1) {
      setIsDrilldownActive(false);
    }
  }, [direction, messageTypes]);

  // Transform API data to chart format with local message type and direction filtering
  const chartData = generateMessagesTrendData(messagesTrendData, messageTypes, direction, selectedManufacturer, isDrilldownData, selectedSbids);
  const chartOptions = getMessagesTrendChartOptions(chartData);

  return (
    <Row id="raymond-messages-trend-chart">
      <Col>
        <Card style={{ minHeight: '500px' }}>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <span>Vehicle Messages Trend</span>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant={isDrilldownActive ? 'primary' : 'outline-primary'}
                  size="sm"
                  style={{ fontSize: '0.8rem', padding: '0.15rem 0.7rem' }}
                  onClick={() => {
                    if (isDrilldownActive) {
                      setMessageTypes([]);
                      setDirection(null);
                      setIsDrilldownActive(false);
                    } else {
                      setMessageTypes(['6002']);
                      setDirection('inbound');
                      setIsDrilldownActive(true);
                    }
                  }}
                  title="Drill down MSGID 6002"
                >
                  Drilldown MSGID 6002
                </Button>
                <span className="badge bg-secondary">last 7 days</span>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Inline Filters for Messages Trend */}
            <Row className="align-items-center mb-3 pb-2 border-bottom g-3">
              <Col xs={12} md="auto">
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted fw-semibold">Direction:</small>
                  <ButtonGroup size="sm">
                    <Button
                      variant={direction === 'inbound' ? 'primary' : 'outline-secondary'}
                      onClick={() => {
                        setDirection(direction === 'inbound' ? null : 'inbound');
                        setMessageTypes([]);
                      }}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                    >
                      Inbound
                    </Button>
                    <Button
                      variant={direction === 'outbound' ? 'primary' : 'outline-secondary'}
                      onClick={() => {
                        setDirection(direction === 'outbound' ? null : 'outbound');
                        setMessageTypes([]);
                      }}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                    >
                      Outbound
                    </Button>
                  </ButtonGroup>
                </div>
              </Col>
              <Col xs={12} md>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted fw-semibold text-nowrap">Message Type:</small>
                    <div style={{ minWidth: '250px', maxWidth: '400px', flex: '1 1 auto' }}>
                      <SearchableSelect
                        options={messageTypeOptions}
                        value={messageTypes}
                        onChange={setMessageTypes}
                        placeholder="Filter by MSGID..."
                        size="sm"
                        isMulti={true}
                        hideBadges={true}
                      />
                    </div>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                    {!direction
                      ? `All directions${messageTypes.length > 0 ? ` • ${messageTypes.length} selected` : ''}`
                      : `${direction === 'inbound' ? 'Inbound' : 'Outbound'}${messageTypes.length > 0 ? ` • ${messageTypes.length} selected` : ''}`
                    }
                    {messageTypes.length > 0 && <FontAwesomeIcon icon={faTimes} size="lg" style={{ cursor: 'pointer', marginLeft: '0.25rem' }} onClick={() => setMessageTypes([])} />}
                  </small>
                  {isDrilldownActive && (
                    <>
                      <div className="d-flex align-items-center gap-2 ms-3">
                        <small className="text-muted fw-semibold text-nowrap">SBID:</small>
                        <div style={{ minWidth: '250px', maxWidth: '400px', flex: '1 1 auto' }}>
                          <SearchableSelect
                            options={sbidOptions}
                            value={selectedSbids}
                            onChange={setSelectedSbids}
                            placeholder="Filter by SBID..."
                            size="sm"
                            isMulti={true}
                            hideBadges={true}
                          />
                        </div>
                      </div>
                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                        {selectedSbids.length === 0 ? `• All selected` : `• ${selectedSbids.length} selected`}
                        {selectedSbids.length > 0 && <FontAwesomeIcon icon={faTimes} size="lg" style={{ cursor: 'pointer', marginLeft: '0.25rem' }} onClick={() => setSelectedSbids([])} />}
                      </small>
                    </>
                  )}
                </div>
              </Col>
            </Row>
            {/* ApexCharts Chart */}
            {loading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-muted">Loading messages trend data...</span>
              </div>
            ) : error ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-danger">Error loading data. Please try again.</span>
              </div>
            ) : chartData.series && chartData.series.length > 0 && chartData.series[0]?.data?.length > 0 ? (
              <Chart
                options={chartOptions}
                series={chartData.series}
                type="bar"
                height={350}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-muted">No data available</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default RaymondMessagesTrendChart;
